/**
 * Two-tier lesson data:
 *  - Index (eager): lite metadata for the list view (id, number, title,
 *    category, scenarioSummary, vocabCount). ~5 KB, ships with the home page.
 *  - Bundle (lazy): full lesson content with sections, dialogue, vocab,
 *    grammar, prose. ~800 KB. Loaded on demand via dynamic import — Vite
 *    splits it into its own chunk.
 */

import indexJson from './lessons-index.json';
import type { Lesson, LessonsBundle, VocabItem } from './types';

export type LessonMeta = {
  id: string;
  number: number;
  title: string;
  category: string;
  scenarioSummary?: string;
  vocabAreas: string[];
  vocabCount: number;
  sectionsCount: number;
};

const index = indexJson as { lessons: LessonMeta[] };
export const lessonsIndex: LessonMeta[] = index.lessons;
export const totalLessonVocabCount: number = lessonsIndex.reduce(
  (s, l) => s + l.vocabCount,
  0,
);

export function lessonMetaByNumber(n: number): LessonMeta | undefined {
  return lessonsIndex.find((l) => l.number === n);
}

let _bundlePromise: Promise<LessonsBundle> | null = null;

export function loadLessons(): Promise<LessonsBundle> {
  if (!_bundlePromise) {
    _bundlePromise = import('./lessons.json').then(
      (m) => (m as unknown as { default: LessonsBundle }).default,
    );
  }
  return _bundlePromise;
}

export async function lessonByNumber(n: number): Promise<Lesson | undefined> {
  const bundle = await loadLessons();
  return bundle.lessons.find((l) => l.number === n);
}

export async function lessonByIdParam(idParam: string): Promise<Lesson | undefined> {
  const bundle = await loadLessons();
  const n = Number.parseInt(idParam, 10);
  if (!Number.isNaN(n)) return bundle.lessons.find((l) => l.number === n);
  return bundle.lessons.find((l) => l.id === idParam);
}

export async function nextLesson(n: number): Promise<Lesson | undefined> {
  return lessonByNumber(n + 1);
}
export async function previousLesson(n: number): Promise<Lesson | undefined> {
  return lessonByNumber(n - 1);
}

export type FlatVocab = VocabItem & {
  kind: 'lesson' | 'wordlist';
  lessonNumber: number;
  lessonTitle: string;
};

/** Flatten all vocab items from all lessons. */
export async function allVocabFlat(): Promise<FlatVocab[]> {
  const bundle = await loadLessons();
  const out: FlatVocab[] = [];
  for (const l of bundle.lessons) {
    for (const section of l.sections) {
      if (section.type !== 'vocab') continue;
      for (const item of section.items) {
        out.push({
          ...item,
          kind: 'lesson',
          lessonNumber: l.number,
          lessonTitle: l.title,
        });
      }
    }
  }
  return out;
}
